import { inject, Injectable, OnDestroy } from '@angular/core';
import { TypedSubject } from '@bcf-vanilla-ts-v1-platforms/platform-worker/browser/types';
import { Observable } from 'rxjs';
import { ExtractedObservable, WorkerBlocChannels } from '../common/types';
import { WorkerBlocId } from '../worker-blocs.type';
import { BrowserSideBlocController } from './browser-side-bloc-controller';
import { Unsubscribable } from './unsubscribable';

type SecondRxKeys<R2 extends WorkerBlocChannels> = keyof R2['rx'];
type SecondSubjectKeys<R2 extends WorkerBlocChannels> = keyof R2['subjects'];

@Injectable()
export class UnsubscribableWithWorkerBlocForService2<R1 extends WorkerBlocChannels, R2 extends WorkerBlocChannels>
  extends Unsubscribable
  implements OnDestroy
{
  private _uniqueId!: string;
  private _workerBlocId!: WorkerBlocId;

  private _secondUniqueId: string | undefined;
  private _secondWorkerBlocId: WorkerBlocId | undefined;

  private _browserSideBlocController: BrowserSideBlocController<R1 | R2> = inject(BrowserSideBlocController);

  protected _initWorkerBloc(workerBlocId: WorkerBlocId): void {
    this._workerBlocId = workerBlocId;
    this._uniqueId = BrowserSideBlocController.getId(workerBlocId);
    this._browserSideBlocController.sendCreateInstance(workerBlocId, this._uniqueId);
    this._sub = this._browserSideBlocController
      .workerBlocInitialized(this._workerBlocId, this._uniqueId)
      .subscribe(() => this._initializedBloc());
  }

  protected _initSecondWorkerBloc(workerBlocId: WorkerBlocId): void {
    this._secondWorkerBlocId = workerBlocId;
    this._secondUniqueId = BrowserSideBlocController.getId(workerBlocId);
    this._browserSideBlocController.sendCreateInstance(workerBlocId, this._secondUniqueId);
    this._sub = this._browserSideBlocController
      .workerBlocInitialized(this._secondWorkerBlocId, this._secondUniqueId)
      .subscribe(() => this._initializedSecondBloc());
  }

  public init(_arg1?: unknown, _arg2?: unknown, _arg4?: unknown): void {}

  protected _initializedBloc(): void {}

  protected _initializedSecondBloc(): void {}

  protected _subTo<K extends keyof R1['rx'], V extends ExtractedObservable<R1['rx'][K]>>(channelRx: K): Observable<V> {
    return this._browserSideBlocController.subTo(this._workerBlocId, this._uniqueId, channelRx);
  }

  protected _subToSecond<K extends SecondRxKeys<R2>, V extends ExtractedObservable<R2['rx'][K]>>(
    channelRx: K
  ): Observable<V> {
    if (!this._secondWorkerBlocId || !this._secondUniqueId) {
      throw new Error('Second worker bloc not initialized; call _initSecondWorkerBloc first.');
    }
    return this._browserSideBlocController.subTo(this._secondWorkerBlocId, this._secondUniqueId, channelRx);
  }

  protected _workerSubject<V extends TypedSubject<R1['subjects'][K]>, K extends keyof R1['subjects']>(
    subjectKey: K
  ): V {
    return this._browserSideBlocController.workerSubject(this._workerBlocId, () => this._uniqueId, subjectKey);
  }

  protected _workerObservable<K extends keyof R1['rx'], V extends ExtractedObservable<R1['rx'][K]>>(
    channelRx: K
  ): Observable<V> {
    return this._browserSideBlocController.subTo(this._workerBlocId, this._uniqueId, channelRx);
  }

  protected _workerSubjectSecond<V extends TypedSubject<R2['subjects'][K]>, K extends SecondSubjectKeys<R2>>(
    subjectKey: K
  ): V {
    if (!this._secondWorkerBlocId) {
      throw new Error('Second worker bloc not initialized; call _initSecondWorkerBloc first.');
    }
    return this._browserSideBlocController.workerSubject(
      this._secondWorkerBlocId,
      () => this._secondUniqueId as string,
      subjectKey
    );
  }

  protected _workerObservableSecond<K extends SecondRxKeys<R2>, V extends ExtractedObservable<R2['rx'][K]>>(
    channelRx: K
  ): Observable<V> {
    if (!this._secondWorkerBlocId || !this._secondUniqueId) {
      throw new Error('Second worker bloc not initialized; call _initSecondWorkerBloc first.');
    }
    return this._browserSideBlocController.subTo(this._secondWorkerBlocId, this._secondUniqueId, channelRx);
  }

  public override ngOnDestroy(): void {
    super.ngOnDestroy();
    this._browserSideBlocController.sendDestroyInstance(this._workerBlocId, this._uniqueId);
    if (this._secondWorkerBlocId && this._secondUniqueId) {
      this._browserSideBlocController.sendDestroyInstance(this._secondWorkerBlocId, this._secondUniqueId);
    }
  }
}
